import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { useCompanyOSIntegrationVariant } from "@mirage/mosaics/BaseProfilePage/utils";
import { ProfileEmployees } from "@mirage/mosaics/UserProfilePage/ProfileEmployees";
import { convertPeopleRecommendationToBasicUserInfo } from "@mirage/mosaics/UserProfilePage/utils";
import { useOrgChart } from "@mirage/service-people/hooks";
import {
  getBirthdays,
  getNewHires,
  getWorkAnniversaries,
} from "@mirage/service-people/service/utils";
import {
  LottieAnimation,
  LottieAnimationType,
} from "@mirage/shared/lottie-animations/LottieAnimation";
import { TwoColumnGrid } from "@mirage/shared/two-column-grid/TwoColumnGrid";
import i18n from "@mirage/translations";
import { NotFound } from "@mirage/webapp/pages/NotFound";

import { Gift } from "@mirage/mosaics/BaseProfilePage/icons/Gift";
import { Megaphone } from "@mirage/mosaics/BaseProfilePage/icons/Megaphone";
import styles from "./PeopleIndexPage.module.css";

const PAGE_UUID = "people-index";

const EmptyContent = ({
  icon,
  subtitle,
  description,
}: {
  icon: React.ReactElement;
  subtitle: string;
  description: string;
}) => (
  <Box
    as="div"
    width="100%"
    textAlign="center"
    className={styles.emptyContentContainer}
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
  >
    <Box as="div" className={styles.emptyContentIcon}>
      {icon}
    </Box>
    <Box as="div">
      <Box
        as={Text}
        color="Text Subtle"
        isBold
        size="large"
        variant="paragraph"
      >
        {subtitle}
      </Box>
    </Box>
    <Box as="div">
      <Box as={Text} color="Text Subtle" variant="paragraph">
        {description}
      </Box>
    </Box>
  </Box>
);

export const PeopleIndexPage = () => {
  const companyOSIntegrationVariant = useCompanyOSIntegrationVariant();
  const orgChart = useOrgChart();

  if (companyOSIntegrationVariant !== "V3") {
    return <NotFound />;
  }

  const newHires = orgChart ? getNewHires(orgChart) : [];
  const workAnniversaries = orgChart ? getWorkAnniversaries(orgChart) : [];
  const birthdays = orgChart ? getBirthdays(orgChart) : [];

  return (
    <Box as="div" className={styles.pageContainer}>
      <Box as="div" className={styles.contentContainer}>
        <TwoColumnGrid
          containerRowGap="Macro Large"
          mainRowGap="Macro Small"
          mainChildren={
            <>
              <ProfileEmployees
                key="new_arrivals"
                pageUuid={PAGE_UUID}
                sectionTitle={i18n.t("people_index_new_arrivals_title")}
                employees={newHires.map(
                  convertPeopleRecommendationToBasicUserInfo,
                )}
                peopleType="new_arrivals"
                emptyContent={
                  <EmptyContent
                    icon={<Megaphone />}
                    subtitle={i18n.t(
                      "people_index_new_arrivals_empty_subtitle",
                    )}
                    description={i18n.t(
                      "people_index_new_arrivals_empty_description",
                    )}
                  />
                }
                alwaysShowAll={false}
              />
              <ProfileEmployees
                key="work_anniversaries"
                pageUuid={PAGE_UUID}
                sectionTitle={i18n.t("people_index_work_anniversaries_title")}
                employees={workAnniversaries.map(
                  convertPeopleRecommendationToBasicUserInfo,
                )}
                peopleType="work_anniversaries"
                emptyContent={
                  <EmptyContent
                    icon={
                      <LottieAnimation
                        animationType={LottieAnimationType.HIGH_FIVE}
                        loop={false}
                        alt={i18n.t(
                          "people_index_work_anniversaries_empty_subtitle",
                        )}
                        style={{ width: "64px" }}
                      />
                    }
                    subtitle={i18n.t(
                      "people_index_work_anniversaries_empty_subtitle",
                    )}
                    description={i18n.t(
                      "people_index_work_anniversaries_empty_description",
                    )}
                  />
                }
                alwaysShowAll={false}
              />
              <ProfileEmployees
                key="birthdays"
                pageUuid={PAGE_UUID}
                sectionTitle={i18n.t("people_index_birthdays_title")}
                employees={birthdays.map(
                  convertPeopleRecommendationToBasicUserInfo,
                )}
                peopleType="birthdays"
                emptyContent={
                  <EmptyContent
                    icon={<Gift />}
                    subtitle={i18n.t("people_index_birthdays_empty_subtitle")}
                    description={i18n.t(
                      "people_index_birthdays_empty_description",
                    )}
                  />
                }
                alwaysShowAll={false}
              />
            </>
          }
        />
      </Box>
    </Box>
  );
};
